<script lang="ts">
  import { faCopy, faPlug } from '@fortawesome/free-solid-svg-icons';
  import { connectTools } from '../connect-tools';
  import Icon from './Icon.svelte';

  /** The MCP server shares the app's address, so its URL is this page's origin plus `/mcp`. */
  const url = `${location.origin}/mcp`;
  // Tools keep sign-in details per server name, so the local server gets its own name and
  // never reuses the deployed one's, or the other way round.
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
  const tools = connectTools(url, local ? 'draw-local' : 'draw');

  let open = $state(false);
  let toolId = $state(tools[0]!.id);
  let copied = $state<string | null>(null);
  let root: HTMLDivElement;

  const tool = $derived(tools.find((each) => each.id === toolId) ?? tools[0]!);

  async function copy(text: string, what: string) {
    await navigator.clipboard.writeText(text);
    copied = what;
  }

  function onWindowPointerDown(event: PointerEvent) {
    if (open && !root.contains(event.target as Node)) open = false;
  }

  function onWindowKeyDown(event: KeyboardEvent) {
    if (open && event.key === 'Escape') open = false;
  }
</script>

<svelte:window onpointerdown={onWindowPointerDown} onkeydown={onWindowKeyDown} />

<div class="connect" bind:this={root}>
  <button
    type="button"
    class="toggle"
    title="Show how to connect an AI assistant to your drawings"
    aria-expanded={open}
    aria-controls="connect-ai"
    onclick={() => {
      open = !open;
      copied = null;
    }}
  >
    <Icon icon={faPlug} />
    Connect AI
  </button>

  <!-- Floats below the header, so opening it never changes the header's height. -->
  {#if open}
    <section id="connect-ai" class="panel" aria-label="Connect AI">
      <p class="lead">AI assistants edit your drawings through this MCP server:</p>
      <div class="row">
        <input type="text" readonly value={url} aria-label="MCP server address" />
        <button type="button" title="Copy the address" onclick={() => copy(url, 'address')}>
          <Icon icon={faCopy} />
          Copy
        </button>
      </div>
      <label class="pick">
        Your AI tool
        <select bind:value={toolId} onchange={() => (copied = null)}>
          {#each tools as each (each.id)}
            <option value={each.id}>{each.name}</option>
          {/each}
        </select>
      </label>
      <ol>
        {#each tool.steps as step (step)}
          <li>{step}</li>
        {/each}
      </ol>
      {#if tool.snippet}
        {@const snippet = tool.snippet}
        <div class="row">
          <textarea
            readonly
            rows={snippet.text.split('\n').length}
            value={snippet.text}
            aria-label={snippet.label}></textarea>
          <button
            type="button"
            title="Copy the {snippet.label}"
            onclick={() => copy(snippet.text, snippet.label)}
          >
            <Icon icon={faCopy} />
            Copy
          </button>
        </div>
      {/if}
      {#if copied}<p class="copied" role="status">Copied the {copied}.</p>{/if}
    </section>
  {/if}
</div>

<style>
  .connect {
    position: relative;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .panel {
    position: absolute;
    top: calc(100% + var(--space-2));
    right: 0;
    z-index: 10;
    width: 30rem;
    max-width: calc(100vw - 2 * var(--space-3));
    padding: var(--space-3);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    box-shadow: 0 4px 16px rgb(0 0 0 / 12%);
    font-size: 0.8125rem;
  }

  .lead {
    margin: 0 0 var(--space-2);
  }

  .row {
    display: flex;
    gap: var(--space-2);
  }

  input,
  textarea {
    flex: 1;
    min-width: 0;
    padding: 5px 8px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
  }

  textarea {
    resize: none;
    white-space: pre;
  }

  .row button {
    align-self: flex-start;
  }

  .pick {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  select {
    flex: 1;
    padding: 4px 6px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
  }

  ol {
    margin: var(--space-2) 0;
    padding-left: 1.1rem;
    color: var(--color-text-muted);
  }

  li {
    margin-bottom: var(--space-1);
  }

  .copied {
    margin: var(--space-2) 0 0;
    color: var(--color-text-muted);
  }
</style>
